import { AfterViewInit, Component, ElementRef, Input, OnInit } from '@angular/core';
import { Feature, MapBrowserEvent } from 'ol';
import { Style, Text } from 'ol/style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import VectorSource from 'ol/source/Vector';
import { EsriJSON } from 'ol/format';
import { environment } from 'src/environments/environment';
import VectorLayer from 'ol/layer/Vector';
import { OcupacionZonaModel } from 'src/app/core/models/ocupacion-zona.model';
import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { TranslateService } from '@ngx-translate/core';
import { OlMapBaseComponent } from '../ol-map-base/ol-map-base.component';
import Layer from 'ol/layer/Layer';

const DEFAULT_HEIGHT = '500px';
const DEFAULT_WIDTH = '500px';
const DEFAULT_LAT = 41.65518;
const DEFAULT_LON = -4.72372;

@Component({
  selector: 'dum-ol-map-barrios',
  templateUrl: './ol-map-barrios.component.html',
  styleUrls: ['./ol-map-barrios.component.scss']
})
export class OlMapBarriosComponent extends OlMapBaseComponent implements OnInit, AfterViewInit {
  @Input() zoom?: number = 11;
  @Input() lat?: number = DEFAULT_LAT;
  @Input() lon?: number = DEFAULT_LON;
  @Input() width?: string | number = DEFAULT_WIDTH;
  @Input() height?: string | number = DEFAULT_HEIGHT;
  @Input() subBarrios?: OcupacionZonaModel[] = [];

  srcIconOrigen: string;
  srcIconDestino: string;
  subBarrioSelected: OcupacionZonaModel;
  green = 'green';
  red = 'red';

  constructor(
    protected elementRef: ElementRef,
    protected translateService: TranslateService) {
    super(elementRef, translateService);
    proj4.defs(
      'EPSG:25830',
      '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
    );
    register(proj4);
  }

  ngAfterViewInit(): void {

    this.initMap('popup-barrios', 'popup-barrios-content', this.lon, this.lat, this.zoom);
    const EsriBarriosLayer = this.getBarriosLayer();
    this.map.addLayer(EsriBarriosLayer);
    this.setClickEvent();
  }

  private getBarriosLayer() {
    const styles = [
      new Style({
        geometry: (feature: Feature) => {
          const nombreSubBarrio: string = feature.get('NOMBRE_SUB');
          const color = this.getColorFeatureBarrio(this.getEstadoSubBarrio(feature.get('OBJECTID')));
          // const color = '28, 43, 65';
          feature.setStyle(new Style({
            stroke: new Stroke({
              color: 'rgb(' + color + ')',
              width: 1,
            }),
            fill: new Fill({
              color: 'rgba(' + color + ', 0.2)',
            }),
            text: new Text({
              font: ' bold 15px Calibri,sans-serif',
              fill: new Fill({ color: '#000' }),
              stroke: new Stroke({
                color: '#fff', width: 2
              }),
              text: nombreSubBarrio.toLocaleUpperCase()
            })
          }
          ));
          return null;
        },
      })];

    const source = new VectorSource({
      format: new EsriJSON(),
      url: environment.capa_barrios
    });

    return new VectorLayer({
      extent: this.map.getView().calculateExtent(),
      source,
      style: styles,
    });
  }

  getEstadoSubBarrio(id) {
    const subBarrio = this.getSubBarrioByIdEsri(id);
    let estado = '';
    if (subBarrio.plazasTotales !== 0) {
      if ((subBarrio.plazasDisponible * 100) / subBarrio.plazasTotales > 25) {
        estado = 'disponible';
      } else if ((subBarrio.plazasDisponible * 100) / subBarrio.plazasTotales < 25
        && (subBarrio.plazasDisponible * 100) / subBarrio.plazasTotales > 10) {
        estado = 'media-ocupacion';
      } else if ((subBarrio.plazasDisponible * 100) / subBarrio.plazasTotales < 10) {
        estado = 'no-disponible';
      }
    } else {
      estado = 'no-disponible';
    }

    return estado;
  }

  getSubBarrioByIdEsri(id): OcupacionZonaModel {
    let subBarrioRe = null;
    this.subBarrios.forEach((suBarrio) => {
      if (suBarrio.idSubBarrioEsri === id) {
        subBarrioRe = suBarrio;
      }
    });
    return subBarrioRe;
  }

  private displayBarrioInfo = (evt: MapBrowserEvent) => {
    const features = [];
    let coordinates = null;
    this.subBarrioSelected = undefined;
    this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
      if (feature.get('NOMBRE_BAR') !== 'EXTRARRADIO') {
        features.push(feature);
      }
    });
    const info = [];
    if (features.length > 0) {
      features.forEach((feature) => {
        this.subBarrios.forEach((subBarrio) => {
          if (subBarrio.idSubBarrioEsri === feature.get('OBJECTID')) {
            this.subBarrioSelected = subBarrio;
            info.push(`${subBarrio.nombre}<br/>`);
            info.push(`Plazas totales : ${subBarrio.plazasTotales}<br/>`);
            info.push(`Plazas ocupadas : ${subBarrio.plazasTotales - subBarrio.plazasDisponible}<br/>`);
            info.push(`Plazas disponibles : ${subBarrio.plazasDisponible}`);
            if (!coordinates) {
              coordinates = this.getHitCoordinates(this.map.getCoordinateFromPixel(evt.pixel), feature.getGeometry());
            }
          }
        });
      }
      );
      setTimeout(() => {
        this.overlayPopUp.setPosition(coordinates ? coordinates : evt.coordinate);
      }, 0);
    } else {
      this.overlayPopUp.setPosition(undefined);
    }
  }

  private setClickEvent() {
    this.map.on('pointermove', (evt) => {
      const hit = this.map.forEachFeatureAtPixel(evt.pixel, (feature: Feature) => {
        return (feature && feature.get('NOMBRE_BAR') && feature.get('NOMBRE_BAR') !== 'EXTRARRADIO');
      });
      if (hit) {
        this.map.getTargetElement().style.cursor = 'pointer';
      } else {
        this.map.getTargetElement().style.cursor = '';
      }
    });
    this.map.on('singleclick', (evt) => {
      this.displayBarrioInfo(evt);
    });
  }

  closePopUp() {
    this.overlayPopUp.setPosition(undefined);
    return false;
  }

  private getColorFeatureBarrio(estado) {
    switch (estado) {
      case 'disponible':
        return '46, 128, 46';
      case 'media-ocupacion':
        return '253, 119, 59';
      case 'no-disponible':
        return '76, 40, 130';
      default:
        return '46, 128, 46';
    }
  }

  protected setSize() {
    if (this.mapEl) {
      const styles = this.mapEl.style;
      styles.height = coerceCssPixelValue(this.height) || DEFAULT_HEIGHT;
      styles.width = coerceCssPixelValue(this.width) || DEFAULT_WIDTH;
    }
  }

  ngOnInit(): void {
  }

}

const cssUnitsPattern = /([A-Za-z%]+)$/;

function coerceCssPixelValue(value: any): string {
  if (value == null) {
    return '';
  }

  return cssUnitsPattern.test(value) ? value : `${value}px`;
}
